import cn from 'classnames';
import * as React from 'react';
import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import * as types from '../../types';
import { AnalyticsEvent, InquiryType as AnalyticsInquiryType, logEvent } from '@weddingspot/ws-analytics';
import CuratedListItem from './curatedListItem';
import { InquiryModal, InquiryType, getSizedImage, WSAppContext } from '@weddingspot/ws-core-components';
import { HBToastContainer } from '../toast';

const styles = require('./barnVenue.scss');
const ringVideo = require('../../assets/icons/ring.mp4');

type BucketRouteProps = {
    data: types.BucketLandingPage;
    curPath: string;
};

const CuratedListPage = (props: BucketRouteProps) => {
    const { userIsAuthenticated, openAuthModal } = React.useContext(WSAppContext);

    const [isContactModalIsOpen, setIsContactModalIsOpen] = useState({});

    useEffect(() => {
        let requestAnimationId: number | null = null;
        const handleScroll = () => {
            const startScroll = 100;
            const finishScroll = window.innerWidth > 480 ? window.innerHeight - 110 : 400;

            const elementsAnimations = [
                {
                    el: document.getElementById('heroImage') as HTMLElement,
                    start: {
                        height: { value: 85, dim: 'vh' },
                        top: { value: 65, dim: 'px' },
                        marginBottom: { value: 15, dim: 'vh' },
                    },
                    finish: {
                        height: { value: 8, dim: 'vh' },
                        top: { value: finishScroll, dim: 'px' },
                        marginBottom: { value: 0, dim: 'vh' },
                    },

                    afterFinish: {
                        top: { value: 0, dim: 'px' },
                        position: { value: 'fixed', dim: '' },
                    },
                    beforeFinish: {
                        // top: {value: 0, dim: 'px'},
                        position: { value: 'absolute', dim: '' },
                    },
                },
                {
                    el: document.querySelector('.barnVenue--headerHeroDescription') as HTMLElement,
                    start: {
                        opacity: { value: 1, dim: '' },
                        transform: { value: 1, format: (value: number) => `scale(${value})` },
                    },
                    finish: {
                        opacity: { value: 0, dim: '' },
                        transform: { value: 0 },
                    },
                },
                {
                    el: document.querySelector('.barnVenue--headerHeroTitle') as HTMLElement,
                    start: {
                        fontSize: {
                            value: window.innerWidth < 480 ? 1.2 : 3,
                            dim: 'em',
                        },

                        maxHeight: { value: 5.2, dim: 'em' },
                        fontWeight: { value: window.innerWidth > 480 ? 600 : 300, dim: '' },
                    },
                    finish: {
                        fontSize: { value: window.innerWidth < 480 ? 1.2 : 2, dim: 'em' },
                        fontWeight: { value: window.innerWidth > 480 ? 100 : 0, dim: '' },
                        maxHeight: { value: 1.5, dim: 'em' },
                    },
                },
                {
                    el: document.querySelector('.barnVenue--headerRingContainer') as HTMLElement,
                    start: {
                        opacity: { value: 1, dim: '' },
                    },
                    finish: {
                        opacity: { value: 0, dim: '' },
                    },
                },
            ];

            const scrollProgressRaw = (window.scrollY - startScroll) / (finishScroll - startScroll);
            const scrollProgress = Math.max(0, Math.min(1, scrollProgressRaw));
            const isFinished = scrollProgress > 0.999;

            elementsAnimations.forEach((elementAnimation) => {
                if (!isFinished && elementAnimation.beforeFinish) {
                    for (const [key, value] of Object.entries(elementAnimation.beforeFinish)) {
                        elementAnimation.el.style[key] = value.value + value.dim;
                    }
                }

                for (const [key] of Object.entries(elementAnimation.start)) {
                    const startProp = elementAnimation.start[key];
                    const finishProp = elementAnimation.finish[key];
                    const progress = (finishProp.value - startProp.value) * scrollProgress;
                    const animationValue = startProp.value + progress;

                    elementAnimation.el.style[key] = startProp.format ? startProp.format(animationValue) : animationValue + startProp.dim;
                }

                if (isFinished && elementAnimation.afterFinish) {
                    for (const [key, value] of Object.entries(elementAnimation.afterFinish)) {
                        elementAnimation.el.style[key] = value.value + value.dim;
                    }
                }
            });

            requestAnimationId = window.requestAnimationFrame(handleScroll);
        };

        window.requestAnimationFrame(handleScroll);

        document.querySelectorAll('.icon-hb-arrowleft-b').forEach((el: HTMLElement, i) => {
            el.style.fontSize = '0.7em';
        });

        document.querySelectorAll('.icon-hb-arrowright-b').forEach((el: HTMLElement, i) => {
            el.style.fontSize = '0.7em';
        });

        return () => window.cancelAnimationFrame(requestAnimationId as number);
    }, []);

    const onLinkAllClick = (e: React.SyntheticEvent<HTMLButtonElement>) => {
        logEvent(
            AnalyticsEvent.EVTNM_CLICKED_WEDDING_VENUE_LIST_ALL,
            {
                Market: props.data.market,
                Region: props.data.region,
                Style: props.data.style,
            },
            () => {
                location.href = props.data.bucket_srp_url;
            }
        );
        e.preventDefault();
    };

    const handleContactCTAClick = (index: number) => {
        setIsContactModalIsOpen({ [index]: true });
    };

    const handleContactCTAClose = (index: number) => {
        setIsContactModalIsOpen({ [index]: false });
    };

    const renderSideNav = () => {
        const data = props.data.related_links || [];

        // @ts-ignore
        return (
            <div className={cn(styles.sideNav)}>
                {!!props.data.bucket && <header>More {props.data.bucket} venues nearby</header>}
                <ul>
                    {data.map((item, index) => {
                        // Currently NavLink doesn't work, but the problem isn't here.
                        // The problem already exist, probably in whole application
                        return (
                            <li key={index}>
                                <a className={cn({ [styles.active]: props.curPath === item.url })} href={item.url}>
                                    {item.name}
                                </a>
                            </li>
                        );
                    })}
                </ul>
            </div>
        );
    };

    const handleInquirySubmitSuccess = (venueId: number) => {
        logEvent(AnalyticsEvent.EVTNM_SUBMIT_INQUIRY, {
            Source: 'Curated List Page',
            'Venue ID': venueId,
            'Inquiry Type': AnalyticsInquiryType.WEDDING,
        });
    };

    const renderCuratedList = () => {
        const list = (props.data.venue_data || []).map((item: types.VenueDataItem, index: number) => {
            return (
                <li key={index}>
                    <CuratedListItem
                        venue={item}
                        market={props.data.market}
                        region={props.data.region}
                        index={index}
                        showSignInModal={openAuthModal}
                        isFavorite={item.is_favorite}
                        handleContactCTAClick={() => handleContactCTAClick(index)}
                    />

                    {isContactModalIsOpen[index] && (
                        <InquiryModal
                            key={index}
                            venueId={item.id}
                            inquiryType={InquiryType.WEDDING}
                            showModal={isContactModalIsOpen[index] || false}
                            userIsAuthed={userIsAuthenticated}
                            handleModalClosed={() => handleContactCTAClose(index)}
                            isDestination={false}
                            showPhoneAndHideReason={false}
                            handleInquirySubmitSuccess={() => {
                                handleInquirySubmitSuccess(item.id);
                            }}
                        />
                    )}
                </li>
            );
        });
        return (
            <>
                <ul className={styles.venueCardList}>{list}</ul>
            </>
        );
    };

    const scroll = () => document.querySelector('.barnVenue--venueCardList')?.scrollIntoView({ behavior: 'smooth' });
    const sizedImage = getSizedImage(props.data.hero_image, 2048);

    const heroBgStyle = {
        backgroundImage: `url(${sizedImage})`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundColor: 'black',
        backgroundSize: 'cover',
    };

    return (
        <div className={styles.wrapper}>
            <HBToastContainer showCloseButton={true} />
            <div className={styles.headerHero} style={heroBgStyle} id={'heroImage'}>
                <div className={styles.headerHeroTitleWrapper} id='heroTitle'>
                    <h1 className={styles.headerHeroTitle} id='heroTitle'>
                        {props.data.title}
                    </h1>
                    <h2 className={styles.headerHeroDescription} id='heroDescription'>
                        {props.data.description}
                    </h2>
                </div>

                <div className={styles.headerRingContainer} onClick={scroll} id={'heroRing'}>
                    <video className={styles.headerRing} autoPlay={true} loop={true} muted={true} playsInline={true}>
                        <source src={ringVideo} type='video/mp4' />
                    </video>
                </div>
            </div>
            <div className={styles.paragraphSection}>
            <ReactMarkdown>{props.data.paragraph_text ?? ''}</ReactMarkdown>
            </div>
            <div className={styles.pageContent}>
                {/*{renderSideNav()}*/}
                {/* remove/hide until we fix this and add related lists links there. */}
                {renderCuratedList()}
            </div>
            <div className={styles.pageFooter}>
                <div className={styles.ctaText}> Not finding what you’re looking for?</div>
                <div className={styles.seeMoreCTA}>
                    <button
                        className={'nx-button nx-button--primary'}
                        onClick={(e: React.SyntheticEvent<HTMLButtonElement>) => onLinkAllClick(e)}
                    >
                        See More Venues
                    </button>
                </div>
                <div className={styles.seoText}>
                    <ReactMarkdown>{props.data.seo_supportive_text ?? ''}</ReactMarkdown>
                </div>
            </div>
        </div>
    );
};

export default CuratedListPage;
